import React from "react";
import styled from "styled-components";
import ItemListQuantityPicker from "./ItemListQuantityPicker";
import SetItemQuantity from "src/core/checkout/components/SetItemQuantity";
import {formatPrice} from "src/core/common/price";
import UpdateItem from "src/core/checkout/components/UpdateItem";
import Error from "src/core/common/components/elements/Error";
import RemoveItemButton from "./RemoveItemButton";
import media from "src/core/common/themes/media";
import PredefinedQuantitySelector from "src/core/checkout/components/cart/ItemsList/PredefinedQuantitySelector";

function ItemEntry({item, error, readOnly}) {
  const {totalPrice, basePrice} = item.getPrices();
  const hasError = Boolean(error);
  return (
    <div>
      <ProductEntryContainer>
        <figure>
          <img className={"cart__item-image"} src={item.getThumbnail()} alt={""} />
        </figure>
        <ItemInfo hasError={hasError}>
          {item.getBrandName() && <ItemBrand>{item.getBrandName()}</ItemBrand>}
          <ItemName>{item.getName()}</ItemName>
          <PricesContainer>
            {totalPrice && <ItemTotalPrice>{formatPrice(totalPrice)}</ItemTotalPrice>}
            {item.hasDiscount() && (
              <ItemTotalPrice isOldPrice={true}>{formatPrice(basePrice)}</ItemTotalPrice>
            )}
          </PricesContainer>
        </ItemInfo>
        <ItemQuantity>
          {!readOnly && (
            <div style={{display: "flex", flexDirection: "column"}}>
              {!item.hasWeightPrices() && (
                <UpdateItem
                  item={item}
                  render={({data, setQuantity, removeItem}) => (
                    <CustomItemListQuantityPicker
                      quantity={data.quantity}
                      removeItem={removeItem}
                      setQuantity={setQuantity}
                    />
                  )}
                />
              )}
              {item.hasWeightPrices() && (
                <WeightPricesContainer>
                  <UpdateItem
                    item={item}
                    render={({data, setWeight}) => (
                      <PredefinedQuantitySelector
                        width={"120px"}
                        product={item.getProduct()}
                        value={data.weight}
                        onChange={setWeight}
                        usePortal={true}
                      />
                    )}
                  />
                  <RemoveItemButton item={item} />
                </WeightPricesContainer>
              )}
            </div>
          )}
          {readOnly && (
            <div style={{display: "flex", flexDirection: "column"}}>
              {!item.hasWeightPrices() && (
                <SetItemQuantity
                  item={item}
                  render={({quantity}) => <QuantityLabel>{quantity}</QuantityLabel>}
                />
              )}
              {item.hasWeightPrices() && (
                <UpdateItem
                  item={item}
                  render={({data}) => <QuantityLabel>{data.weight.label}</QuantityLabel>}
                />
              )}
            </div>
          )}
        </ItemQuantity>
      </ProductEntryContainer>
      {hasError && <Error>{error.detail}</Error>}
    </div>
  );
}

const WeightPricesContainer = styled.div`
  > *:last-child {
    margin-top: 8px;
  }
`;

let QuantityLabel = styled.div`
  min-width: 30px;
  min-height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({backgroundColor}) =>
    backgroundColor ? backgroundColor : "#fdfefe"};
  border: 1px solid ${({outlineColor}) => (outlineColor ? outlineColor : "#f0f5f6")};
  color: ${({color}) => (color ? color : "#020202")};
  font-size: ${({theme}) => theme.v2.typography.sizing.xl.lg};
  border-radius: 15px;
  box-sizing: border-box;
  padding: 0 8px;
`;

const ProductEntryContainer = styled.div`
  background-color: white;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: flex-start;

  figure > img {
    height: 75px;
    width: 75px;
    border-radius: ${({theme}) => theme.v2.decoration.border.radius.default};
    object-fit: contain;
  }

  figure {
    margin: 0 ${({theme}) => theme.v2.spacing(2)} 0 0;
  }
`;
const ItemText = styled.span`
  margin: 0 0 4px 0;
`;
let ItemBrand = styled(ItemText)`
  font-weight: ${({theme}) =>
    theme.v2.overrides?.cart?.typography?.body?.weight?.medium ||
    theme.v2.typography.body.weight.medium};
  text-transform: uppercase;
  font-size: ${({theme}) => theme.v2.typography.sizing.xs.lg};
  color: ${({theme}) => theme.v2.color.primary};
`;

const ItemName = styled(ItemText)`
  font-size: ${({theme}) => theme.v2.typography.sizing.m.lg};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ItemTotalPrice = styled(ItemText)`
  font-size: ${({theme}) => theme.v2.typography.sizing.m.lg};
  font-weight: ${({theme}) =>
    theme.v2.overrides?.cart?.typography?.body?.weight?.medium ||
    theme.v2.typography.bodyText1.weight.medium};
  text-decoration: ${({isOldPrice}) => (isOldPrice ? "line-through" : "none")};
  color: ${({isOldPrice}) => (isOldPrice ? "#969696" : "#020202")};
`;

const ItemInfo = styled.div`
  display: flex;
  width: 100%;
  max-width: 150px;
  ${media.down("md")} {
    max-width: 120px;
  }
  padding-right: ${({theme}) => theme.v2.spacing(10)};
  box-sizing: border-box;
  flex-direction: column;

  ${ItemName}, ${ItemText}, ${ItemTotalPrice} {
    ${({hasError}) => (hasError ? "color: red !important;" : "")}
  }
`;

const ItemQuantity = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;
const PricesContainer = styled.div`
  display: flex;
  align-items: center;
  > :not(:first-child) {
    margin-left: ${({theme}) => theme.v2.spacing(2)};
  }
`;

let CustomItemListQuantityPicker = styled(ItemListQuantityPicker)`
  color: ${({color}) => color};
  background-color: ${({backgroundColor}) => backgroundColor};
`;

export default ItemEntry;
